import { GetServerSideProps, NextPage } from 'next';
import dynamic from 'next/dynamic';

import {
  cacheControl,
  filterMarkets,
  getCanonicalUrl,
  getHrefLanguages,
} from '@hultafors/shared/helpers';

import {
  datoApi,
  getContentAreaData,
  markets,
} from '@hultafors/hultaforsgroup/api';
import { NEWS_PAGE_SIZE } from '@hultafors/hultaforsgroup/helpers';
import {
  ContentAreaData,
  GlobalFields,
  NewsListPageContentFragment,
  PageProps,
} from '@hultafors/hultaforsgroup/types';

import { prodDomains } from '../../next.domains';

const Page = dynamic(() =>
  import('@hultafors/hultaforsgroup/components').then((module) => module.Page),
);
const ContentArea = dynamic(() =>
  import('@hultafors/hultaforsgroup/components').then(
    (module) => module.ContentArea,
  ),
);

type NewsListPageProps = PageProps<NewsListPageContentFragment>;

const NewsListPage: NextPage<NewsListPageProps> = ({
  canonical,
  content,
  hrefLanguages,
  contentAreaData,
}) => {
  return (
    <Page seo={content.seo} canonical={canonical} hrefLanguages={hrefLanguages}>
      <ContentArea content={content?.contentArea} data={contentAreaData} />
    </Page>
  );
};

export const getServerSideProps: GetServerSideProps<
  NewsListPageProps
> = async ({ locale, res }) => {
  try {
    const dato = datoApi(locale);

    const pageSize = Math.min(100, NEWS_PAGE_SIZE || 6);

    const canonical = getCanonicalUrl(
      locale,
      prodDomains,
      'www.hultaforsgroup.com',
      '/news',
    );

    const {
      data: { newsListPage: content, ...globalFields },
    } = await dato.getNewsListPage(pageSize);

    if (!content || content === null) {
      return {
        notFound: true,
      };
    }

    const contentAreaData: ContentAreaData = await getContentAreaData(
      dato,
      content.contentArea,
    );

    const hrefLanguages = getHrefLanguages(
      null,
      filterMarkets(markets, 'HULTAFORSGROUP'),
      content?.pageLocales,
    );

    res.setHeader('Cache-Control', cacheControl.page);
    const props: NewsListPageProps = {
      canonical,
      content,
      contentAreaData,
      hrefLanguages,
      ...(globalFields as unknown as GlobalFields),
    };

    return { props };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};

export default NewsListPage;
